<template>
  <div class="user-panel">

    <!-- Top Header -->
    <header class="panel-header">
      <div class="header-left">
        <button class="mobile-menu-btn" @click="sidebarOpen = !sidebarOpen">
          <fa-icon :icon="['fas', 'bars']" />
        </button>
        <img src="@/assets/logo_text.png" class="panel-logo" alt="Tyng & Co" />
      </div>

      <div class="header-actions">
        <span
          v-if="sessionCountdown > 0"
          class="session-timer"
          :class="{ 'session-warning': sessionCountdown <= 300 }"
        >
          <fa-icon :icon="['fas', 'clock']" />
          {{ formattedCountdown }}
        </span>

        <div class="header-right" ref="profileArea">
          <div class="user-avatar-btn" @click="toggleProfile">
            <div class="avatar-circle">{{ companyInitial }}</div>
            <span class="avatar-name">{{ companyName }}</span>
            <fa-icon :icon="['fas', 'chevron-down']" class="avatar-chevron" :class="{ open: profileOpen }" />
          </div>

          <transition name="slide-down">
            <div v-if="profileOpen" class="profile-dropdown">
              <div class="pd-header" @click="activeSection = 'profile'; toggleProfile()">
                <div class="avatar-circle large">{{ companyInitial }}</div>
                <div class="pd-info">
                  <p class="pd-name">{{ companyName }}</p>
                  <p class="pd-email">{{ companyUsername }}</p>
                </div>
              </div>
              <div class="pd-divider"></div>
              <button class="pd-logout" @click="logout">
                <fa-icon :icon="['fas', 'sign-out-alt']" />
                Sign Out
              </button>
            </div>
          </transition>
        </div>
      </div>
    </header>

    <!-- Body -->
    <div class="panel-body">

      <div
        v-if="sidebarOpen"
        class="sidebar-overlay"
        @click="sidebarOpen = false"
      ></div>

      <!-- Sidebar -->
      <aside class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-inner">
          <p class="sidebar-label">MENU</p>
          <nav>
            <a
              v-for="item in navItems"
              :key="item.id"
              class="nav-item"
              :class="{ active: activeSection === item.id }"
              @click="selectSection(item.id)"
            >
              <span class="nav-icon-wrap">
                <fa-icon :icon="item.icon" />
              </span>
              <span class="nav-label">{{ item.label }}</span>
            </a>
          </nav>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="panel-main">

        <!-- Overview -->
        <template v-if="activeSection === 'overview'">
          <div class="page-header">
            <h1>Welcome back, {{ companyName }}!</h1>
            <p>Here's an overview of your services with Tyng &amp; Co.</p>
          </div>

          <div class="service-grid">
            <div
              v-for="item in serviceItems"
              :key="item.id"
              class="service-card"
              @click="selectSection(item.id)"
            >
              <div class="sc-icon-wrap" :style="{ background: item.iconBg }">
                <fa-icon :icon="item.icon" :style="{ color: item.iconColor }" />
              </div>
              <div class="sc-body">
                <h3>{{ item.label }}</h3>
                <p>{{ item.desc }}</p>
              </div>
              <fa-icon :icon="['fas', 'chevron-right']" class="sc-arrow" />
            </div>
          </div>
        </template>

        <!-- Company Registration -->
        <template v-if="activeSection === 'company'">
          <div class="page-header">
            <h1>Company Registration</h1>
            <p>Submit your company details using the form below.</p>
          </div>

          <div class="up-reg-cta">
            <div class="up-reg-cta-body">
              <fa-icon :icon="['fas', 'file-alt']" class="up-reg-cta-icon" />
              <div>
                <h3>Company Registration Form</h3>
                <p>Fill in your company details via our online registration form.</p>
              </div>
            </div>
            <button class="up-reg-cta-btn" @click="openRegistrationForm">
              <fa-icon :icon="['fas', 'external-link-alt']" />
              Open Registration Form
            </button>
          </div>

          <div class="up-cosec-section">
            <h2 class="up-cosec-heading">Submitted Registration Data</h2>
            <p class="up-cosec-sub">Your data appears here once our team has linked and processed your submission.</p>

            <div v-if="cosecLoading" class="doc-loading">
              <fa-icon :icon="['fas', 'spinner']" spin /> Loading…
            </div>

            <template v-else-if="cosecData">

              <!-- Company Information -->
              <div class="up-info-card">
                <div class="up-info-card-head">
                  <fa-icon :icon="['fas', 'building']" class="up-info-head-icon" />
                  <span>Company Information</span>
                </div>
                <div class="up-info-grid">
                  <div class="up-info-row up-info-row-full">
                    <span class="up-info-label">Client</span>
                    <span class="up-info-val">{{ cosecData.client || '—' }}</span>
                  </div>
                  <div v-if="cosecData.company_name_explanation" class="up-info-row up-info-row-full">
                    <span class="up-info-label">Name Explanation</span>
                    <span class="up-info-val">{{ cosecData.company_name_explanation }}</span>
                  </div>
                  <div class="up-info-row up-info-row-full">
                    <span class="up-info-label">Nature of Business</span>
                    <span class="up-info-val">{{ cosecData.nature_of_business || '—' }}</span>
                  </div>
                  <div class="up-info-row up-info-row-full">
                    <span class="up-info-label">Business Address</span>
                    <span class="up-info-val">{{ cosecData.business_address || '—' }}</span>
                  </div>
                  <div class="up-info-row">
                    <span class="up-info-label">Phone</span>
                    <span class="up-info-val">{{ cosecData.business_phone || '—' }}</span>
                  </div>
                  <div class="up-info-row">
                    <span class="up-info-label">Email</span>
                    <span class="up-info-val">{{ cosecData.business_email || '—' }}</span>
                  </div>
                  <div class="up-info-row">
                    <span class="up-info-label">Initial Capital</span>
                    <span class="up-info-val">{{ cosecData.initial_capital ? 'RM ' + cosecData.initial_capital : '—' }}</span>
                  </div>
                  <div class="up-info-row">
                    <span class="up-info-label">Price / Share</span>
                    <span class="up-info-val">{{ cosecData.price_per_share ? 'RM ' + cosecData.price_per_share : '—' }}</span>
                  </div>
                  <div class="up-info-row">
                    <span class="up-info-label">Total Shares</span>
                    <span class="up-info-val">{{ cosecData.total_shares || '—' }}</span>
                  </div>
                  <div class="up-info-row">
                    <span class="up-info-label">Year End</span>
                    <span class="up-info-val">{{ cosecData.year_end || '—' }}</span>
                  </div>
                </div>
              </div>

              <!-- Directors -->
              <div v-if="directors.length" class="up-info-card">
                <div class="up-info-card-head">
                  <fa-icon :icon="['fas', 'user-tie']" class="up-info-head-icon" />
                  <span>Directors</span>
                  <span class="up-info-badge">{{ directors.length }}</span>
                </div>
                <div
                  v-for="(dir, i) in directors"
                  :key="'dir-' + i"
                  class="up-person"
                  :class="{ 'up-person-primary': i === 0 }"
                >
                  <div class="up-person-head">
                    <div class="up-avatar">{{ dir.full_name ? dir.full_name.charAt(0).toUpperCase() : '?' }}</div>
                    <div>
                      <div class="up-person-name">
                        {{ dir.full_name }}
                        <span v-if="i === 0" class="up-tag">Primary</span>
                      </div>
                      <div class="up-person-sub">Director {{ dir.sort_order }}</div>
                    </div>
                  </div>
                  <div class="up-info-grid">
                    <div class="up-info-row"><span class="up-info-label">Nationality</span><span class="up-info-val">{{ dir.nationality || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Gender</span><span class="up-info-val">{{ dir.gender || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">NRIC</span><span class="up-info-val up-mono">{{ dir.nric_number || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Passport</span><span class="up-info-val up-mono">{{ dir.passport_number || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Phone</span><span class="up-info-val">{{ dir.phone || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Email</span><span class="up-info-val">{{ dir.email || '—' }}</span></div>
                    <div class="up-info-row up-info-row-full"><span class="up-info-label">Address</span><span class="up-info-val">{{ dir.residential_address || '—' }}</span></div>
                  </div>
                </div>
              </div>

              <!-- Shareholders -->
              <div v-if="shareholders.length" class="up-info-card">
                <div class="up-info-card-head">
                  <fa-icon :icon="['fas', 'users']" class="up-info-head-icon" />
                  <span>Shareholders</span>
                  <span class="up-info-badge">{{ shareholders.length }}</span>
                </div>
                <div v-for="(sh, i) in shareholders" :key="'sh-' + i" class="up-person">
                  <div class="up-person-head">
                    <div class="up-avatar up-avatar-sh">{{ sh.full_name ? sh.full_name.charAt(0).toUpperCase() : '?' }}</div>
                    <div>
                      <div class="up-person-name">
                        {{ sh.full_name }}
                        <span v-if="sh.is_also_director === 'Yes'" class="up-tag up-tag-dir">Also Director</span>
                      </div>
                      <div class="up-person-sub">Shareholder {{ sh.sort_order }} · {{ sh.shares_held || '?' }} shares</div>
                    </div>
                  </div>
                  <div class="up-info-grid">
                    <div class="up-info-row"><span class="up-info-label">Nationality</span><span class="up-info-val">{{ sh.nationality || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Gender</span><span class="up-info-val">{{ sh.gender || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">NRIC</span><span class="up-info-val up-mono">{{ sh.nric_number || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Passport</span><span class="up-info-val up-mono">{{ sh.passport_number || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Phone</span><span class="up-info-val">{{ sh.phone || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Email</span><span class="up-info-val">{{ sh.email || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Shares</span><span class="up-info-val">{{ sh.shares_held || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Source of Funds</span><span class="up-info-val">{{ sh.source_of_funds || '—' }}</span></div>
                  </div>
                </div>
              </div>

              <!-- Next of Kin -->
              <div v-if="nextOfKin.length" class="up-info-card">
                <div class="up-info-card-head">
                  <fa-icon :icon="['fas', 'heart']" class="up-info-head-icon" />
                  <span>Next of Kin</span>
                </div>
                <div v-for="(nok, i) in nextOfKin" :key="'nok-' + i" class="up-person">
                  <div class="up-person-head">
                    <div class="up-avatar up-avatar-nok">{{ nok.full_name ? nok.full_name.charAt(0).toUpperCase() : '?' }}</div>
                    <div>
                      <div class="up-person-name">{{ nok.full_name }}</div>
                      <div class="up-person-sub">{{ nok.relationship || 'Next of Kin' }}</div>
                    </div>
                  </div>
                  <div class="up-info-grid">
                    <div class="up-info-row"><span class="up-info-label">NRIC</span><span class="up-info-val up-mono">{{ nok.nric_number || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Phone</span><span class="up-info-val">{{ nok.phone || '—' }}</span></div>
                    <div class="up-info-row"><span class="up-info-label">Email</span><span class="up-info-val">{{ nok.email || '—' }}</span></div>
                    <div class="up-info-row up-info-row-full"><span class="up-info-label">Address</span><span class="up-info-val">{{ nok.residential_address || '—' }}</span></div>
                  </div>
                </div>
              </div>

            </template>

            <empty-state
              v-else
              :icon="['fas', 'clipboard-list']"
              icon-bg="#F0FDF4"
              icon-color="#16A34A"
              title="Not yet processed"
              message="Your data will appear here once submitted and processed by our team."
            />
          </div>
        </template>

        <!-- Tax -->
        <template v-if="activeSection === 'tax'">
          <div class="page-header">
            <h1>Tax</h1>
            <p>Track and manage your tax submissions and filings.</p>
          </div>
          <empty-state
            :icon="['fas', 'percent']"
            icon-bg="#FFFBEB"
            icon-color="#D97706"
            title="No records yet"
            message="Your tax records will appear here once available."
          />
        </template>

        <!-- Accounting -->
        <template v-if="activeSection === 'sst'">
          <div class="page-header">
            <h1>Accounting</h1>
            <p>View your financial reports and statements.</p>
          </div>
          <empty-state
            :icon="['fas', 'chart-bar']"
            icon-bg="#ECFDF5"
            icon-color="#059669"
            title="No records yet"
            message="Your SST records will appear here once available."
          />
        </template>

        <!-- Payroll -->
        <template v-if="activeSection === 'payroll'">
          <div class="page-header">
            <h1>Payroll</h1>
            <p>Manage employee payroll and reports.</p>
          </div>
          <empty-state
            :icon="['fas', 'users']"
            icon-bg="#F5F3FF"
            icon-color="#7C3AED"
            title="No records yet"
            message="Your payroll records will appear here once available."
          />
        </template>

        <!-- Profile -->
        <template v-if="activeSection === 'profile'">
          <div class="page-header">
            <h1>My Profile</h1>
            <p>View your account details and manage your password.</p>
          </div>

          <div v-if="profileLoading && !profileData" class="doc-loading">
            <fa-icon :icon="['fas', 'spinner']" spin /> Loading profile…
          </div>

          <div v-if="profileData" class="up-info-card">
            <div class="up-info-card-head">
              <fa-icon :icon="['fas', 'user']" class="up-info-head-icon" />
              <span>Account Information</span>
            </div>
            <div class="up-info-grid">
              <div class="up-info-row up-info-row-full">
                <span class="up-info-label">Client</span>
                <span class="up-info-val">{{ profileData.client || '—' }}</span>
              </div>
              <div class="up-info-row">
                <span class="up-info-label">Username</span>
                <span class="up-info-val up-mono">{{ profileData.username || '—' }}</span>
              </div>
              <div class="up-info-row">
                <span class="up-info-label">Email</span>
                <span class="up-info-val">{{ profileData.email || '—' }}</span>
              </div>
              <div class="up-info-row">
                <span class="up-info-label">Phone</span>
                <span class="up-info-val">{{ profileData.phone || '—' }}</span>
              </div>
              <div class="up-info-row">
                <span class="up-info-label">Member Since</span>
                <span class="up-info-val">{{ formatDocDate(profileData.created_at) }}</span>
              </div>
            </div>
          </div>

          <!-- Change password card -->
          <div class="profile-pw-card">
            <div class="profile-pw-head">
              <fa-icon :icon="['fas', 'key']" class="profile-pw-icon" />
              <div>
                <h3>Change Password</h3>
                <p>You will remain signed in after updating your password.</p>
              </div>
            </div>

            <div class="profile-pw-form">
              <password-input v-model="pwForm.current" label="Current password" :required="true" placeholder="Enter current password" />
              <password-input v-model="pwForm.newPw" label="New password" :required="true" placeholder="Min. 8 characters" :min-length="8" />
              <password-input v-model="pwForm.confirm" label="Confirm new password" :required="true" placeholder="Repeat new password" :match-value="pwForm.newPw" />

              <div v-if="pwError" class="up-alert up-alert-error">
                <fa-icon :icon="['fas', 'exclamation-circle']" /> {{ pwError }}
              </div>
              <div v-if="pwSuccess" class="up-alert up-alert-success">
                <fa-icon :icon="['fas', 'check-circle']" /> {{ pwSuccess }}
              </div>

              <button
                class="up-submit-btn"
                :disabled="!canChangePassword || pwLoading"
                @click="changePassword"
              >
                <fa-icon :icon="pwLoading ? ['fas', 'spinner'] : ['fas', 'key']" :spin="pwLoading" />
                {{ pwLoading ? 'Updating…' : 'Update Password' }}
              </button>
            </div>
          </div>
        </template>

      </main>
    </div>

    <session-modal :show="showSessionExpiredModal" @continue="continueSession" @signout="logout" />

  </div>
</template>

<script>
import PasswordInput from '@/components/form/PasswordInput.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import EmptyState from '@/components/shared/EmptyState.vue'
import fetchOnceMixin from '@/mixins/fetchOnceMixin'

export default {
  name: 'UserPanel',
  mixins: [fetchOnceMixin],
  components: { PasswordInput, SessionModal, EmptyState },
  data () {
    return {
      profileOpen: false,
      sidebarOpen: false,
      activeSection: 'overview',
      showSessionExpiredModal: false,
      pendingApiRetry: null,
      clientUser: null,
      sessionCountdown: 0,
      sessionTimer: null,
      // Cosec data
      cosecData: null,
      cosecLoading: false,
      // Profile
      profileData: null,
      profileLoading: false,
      pwForm: { current: '', newPw: '', confirm: '' },
      pwLoading: false,
      pwError: '',
      pwSuccess: '',
      navItems: [
        { id: 'overview', label: 'Overview', icon: ['fas', 'th-large'] },
        { id: 'company', label: 'Company Reg.', icon: ['fas', 'industry'] },
        { id: 'tax', label: 'Tax', icon: ['fas', 'percent'] },
        { id: 'sst', label: 'SST', icon: ['fas', 'chart-bar'] },
        { id: 'payroll', label: 'Payroll', icon: ['fas', 'users'] },
        { id: 'profile', label: 'My Profile', icon: ['fas', 'user'] }
      ],
      serviceItems: [
        { id: 'company', label: 'Company Registration', desc: 'Company secretarial and registration matters', icon: ['fas', 'industry'], iconBg: '#EFF6FF', iconColor: '#3B82F6' },
        { id: 'tax', label: 'Tax', desc: 'Tax filing and submission tracking', icon: ['fas', 'percent'], iconBg: '#FFFBEB', iconColor: '#D97706' },
        { id: 'sst', label: 'SST', desc: 'Financial reports and statements', icon: ['fas', 'chart-bar'], iconBg: '#ECFDF5', iconColor: '#059669' },
        { id: 'payroll', label: 'Payroll', desc: 'Employee payroll management', icon: ['fas', 'users'], iconBg: '#F5F3FF', iconColor: '#7C3AED' }
      ]
    }
  },
  computed: {
    companyName () { return this.clientUser?.client || 'Client' },
    companyUsername () { return this.clientUser?.username || '' },
    companyInitial () { return this.companyName.charAt(0).toUpperCase() },
    directors () { return (this.cosecData?.persons || []).filter(p => p.role === 'director') },
    shareholders () { return (this.cosecData?.persons || []).filter(p => p.role === 'shareholder') },
    nextOfKin () { return (this.cosecData?.persons || []).filter(p => p.role === 'next_of_kin') },
    canChangePassword () {
      return (
        this.pwForm.current.trim() !== '' &&
        this.pwForm.newPw.length >= 8 &&
        this.pwForm.newPw === this.pwForm.confirm
      )
    },
    formattedCountdown () {
      const s = Math.max(0, this.sessionCountdown)
      if (s >= 3600) {
        const h = Math.floor(s / 3600)
        const m = Math.floor((s % 3600) / 60)
        const sec = s % 60
        return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
      }
      const m = Math.floor(s / 60)
      return `${m}:${(s % 60).toString().padStart(2, '0')}`
    }
  },
  watch: {
    activeSection (val) {
      if (val === 'company') this.fetchOnce('cosec', this.fetchCosecData)
      if (val === 'profile') this.fetchOnce('profile', this.fetchProfile)
    }
  },
  mounted () {
    if (!localStorage.getItem('client_access_token')) {
      this.$router.push(`/${this.$i18n.locale}/login`)
      return
    }
    const raw = localStorage.getItem('client_user')
    if (raw) { try { this.clientUser = JSON.parse(raw) } catch (e) {} }
    window.addEventListener('storage', this.handleStorageChange)
    document.addEventListener('click', this.handleOutsideClick)
    this.startSessionTimer()
  },
  beforeDestroy () {
    this.clearSessionTimer()
    window.removeEventListener('storage', this.handleStorageChange)
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    selectSection (id) { this.activeSection = id; this.sidebarOpen = false },
    toggleProfile () { this.profileOpen = !this.profileOpen },
    handleOutsideClick (e) {
      if (this.$refs.profileArea && !this.$refs.profileArea.contains(e.target)) this.profileOpen = false
    },
    handleStorageChange (e) {
      if (e.key === 'client_access_token' && !e.newValue) this.$router.push(`/${this.$i18n.locale}/login`)
    },

    // ── Session ─────────────────────────────────────────────────
    decodeJWT (token) {
      try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
        return JSON.parse(atob(base64))
      } catch { return null }
    },
    startSessionTimer () {
      this.clearSessionTimer()
      const token = localStorage.getItem('client_access_token')
      if (!token) return
      const payload = this.decodeJWT(token)
      if (!payload?.exp) return
      const secondsLeft = payload.exp - Math.floor(Date.now() / 1000)
      if (secondsLeft <= 0) { this.showSessionExpiredModal = true; return }
      this.sessionCountdown = secondsLeft
      this.sessionTimer = setInterval(() => {
        if (!localStorage.getItem('client_access_token')) { this.clearSessionTimer(); this.logout(); return }
        this.sessionCountdown--
        if (this.sessionCountdown <= 0) { this.clearSessionTimer(); this.showSessionExpiredModal = true }
      }, 1000)
    },
    clearSessionTimer () {
      if (this.sessionTimer) { clearInterval(this.sessionTimer); this.sessionTimer = null }
    },
    async logout () {
      this.clearSessionTimer()
      try {
        await fetch('https://tyng-ca.com.my/api/client/logout.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: localStorage.getItem('client_refresh_token') })
        })
      } catch (err) { console.error('Logout API failed:', err) }
      localStorage.removeItem('client_access_token')
      localStorage.removeItem('client_refresh_token')
      localStorage.removeItem('client_user')
      this.$router.push(`/${this.$i18n.locale}/login`)
    },
    async refreshToken () {
      const refreshToken = localStorage.getItem('client_refresh_token')
      if (!refreshToken) { this.showSessionExpiredModal = true; return false }
      try {
        const res = await fetch('https://tyng-ca.com.my/api/client/refresh-token.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken })
        })
        const data = await res.json()
        if (data.success) {
          localStorage.setItem('client_access_token', data.token)
          if (data.refresh_token) localStorage.setItem('client_refresh_token', data.refresh_token)
          return true
        }
        if (res.status === 403) { this.showSessionExpiredModal = false; this.logout(); return false }
        this.showSessionExpiredModal = false
        this.logout()
        return false
      } catch (err) { console.error('Refresh failed:', err); this.showSessionExpiredModal = true; return false }
    },
    async apiFetch (url, options = {}) {
      const token = localStorage.getItem('client_access_token')
      const res = await fetch(url, {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${token}` }
      })
      if (res.status === 401) { this.pendingApiRetry = { url, options }; this.showSessionExpiredModal = true }
      return res
    },
    async continueSession () {
      const refreshed = await this.refreshToken()
      if (refreshed) {
        this.showSessionExpiredModal = false
        this.startSessionTimer()
        if (this.pendingApiRetry) {
          const { url, options } = this.pendingApiRetry
          this.pendingApiRetry = null
          await this.apiFetch(url, options)
        }
      }
    },

    // ── Registration form ────────────────────────────────────────
    openRegistrationForm () {
      window.open(`/${this.$i18n.locale}/company-registration-form`, '_blank', 'noopener,noreferrer')
    },

    // ── Cosec data ───────────────────────────────────────────────
    async fetchCosecData () {
      this.cosecLoading = true
      try {
        const res = await this.apiFetch('https://tyng-ca.com.my/api/client/cosec/get.php')
        const data = await res.json()
        if (data.success) this.cosecData = data.data
      } catch (err) { console.error('Failed to load cosec data:', err) } finally { this.cosecLoading = false }
    },

    // ── Profile ──────────────────────────────────────────────────
    async fetchProfile () {
      this.profileLoading = true
      try {
        const res = await this.apiFetch('https://tyng-ca.com.my/api/client/profile/get.php')
        const data = await res.json()
        if (data.success && JSON.stringify(data.data) !== JSON.stringify(this.profileData)) {
          this.profileData = data.data
        }
      } catch (err) { console.error('Failed to load profile:', err) } finally { this.profileLoading = false }
    },
    async changePassword () {
      if (!this.canChangePassword || this.pwLoading) return
      this.pwLoading = true
      this.pwError = ''
      this.pwSuccess = ''
      try {
        const res = await this.apiFetch('https://tyng-ca.com.my/api/client/profile/change-password.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ current_password: this.pwForm.current, new_password: this.pwForm.newPw })
        })
        const data = await res.json()
        if (data.success) {
          this.pwSuccess = data.message || 'Password updated successfully.'
          this.pwForm = { current: '', newPw: '', confirm: '' }
        } else {
          this.pwError = data.message || 'Failed to update password.'
        }
      } catch (err) {
        this.pwError = 'Network error. Please try again.'
      } finally {
        this.pwLoading = false
      }
    },

    // ── Helpers ──────────────────────────────────────────────────
    formatDocDate (str) {
      if (!str) return ''
      const d = new Date(str)
      return isNaN(d) ? str : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }
  }
}
</script>
